import { MessagingPlatform } from '@this+that/models';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { createIntegrationsSlice } from './actions';
import { IntegrationsStore } from './types';
import type { IntegrationAccount } from '../../../models/integrationAccount';
import { ZustandStores } from '../../utils';
import { createIndexedDBStorage } from '../../utils/indexedDBAdapter';

export const partializeIntegrationsState = (state: IntegrationsStore) => {
  // teamGithubInstallationRequests is server-authoritative and not team-scoped in
  // the cache: rehydrating it would show one team's pending org on another.
  // currentEmailFilters is derived at runtime; a persisted one is a stale filter from the last session.
  const {
    isLoading,
    isDraftSaving,
    draftSavingSessionIds,
    teamGithubInstallationRequests,
    currentEmailFilters,
    ...rest
  } = state;
  return rest;
};

export const mergeHydratedIntegrationsState = (
  persisted: unknown,
  current: IntegrationsStore,
): IntegrationsStore => ({
  ...current,
  ...(persisted as Partial<IntegrationsStore>),
  currentEmailFilters: current.currentEmailFilters,
});

export const INTEGRATIONS_PERSIST_VERSION = 1;

type LegacyAccount = Omit<IntegrationAccount, 'address'> &
  Partial<Pick<IntegrationAccount, 'address'>>;

const deriveAccountAddress = (account: LegacyAccount): string => {
  if (account.address) {
    return account.address;
  }
  if (account.platform === MessagingPlatform.GMAIL) {
    return account.identifier;
  }
  if (account.platform === MessagingPlatform.OUTLOOK) {
    return account.displayName;
  }
  return '';
};

export const migrateIntegrationsPersistedState = (
  persisted: unknown,
  version: number,
): Partial<IntegrationsStore> => {
  const state = (persisted ?? {}) as Partial<IntegrationsStore>;
  if (version >= INTEGRATIONS_PERSIST_VERSION || !state.integrationAccounts) {
    return state;
  }
  return {
    ...state,
    integrationAccounts: (state.integrationAccounts as LegacyAccount[]).map(
      (account) => ({ ...account, address: deriveAccountAddress(account) }),
    ),
  };
};

export const useIntegrationsStore = create<IntegrationsStore>()(
  persist(
    (set, get, store) => ({
      ...createIntegrationsSlice(set, get, store),
    }),
    {
      name: ZustandStores.INTEGRATIONS,
      version: INTEGRATIONS_PERSIST_VERSION,
      migrate: migrateIntegrationsPersistedState,
      partialize: partializeIntegrationsState,
      merge: mergeHydratedIntegrationsState,
      storage: createJSONStorage(() => createIndexedDBStorage()),
    },
  ),
);
